<template>
    <div class="track-tech-page">
		<landing-page-navbar></landing-page-navbar>

        <!-- Loading state -->
        <div class="mt-5 pt-5" v-if="loading && !showForm">
            <loading-state class="mt-5"></loading-state>
            <h4 class="mb-4 text-center">{{ $t('bp.TrackTechnician.loading') }}</h4>
        </div>

        <div class="booking-view" v-if="!loading">
            <div class="content-container">
                <div class="content h-auto">
                    <!-- Order not found -->
                    <div class="container" v-if="showForm">
                        <div class="card">
                            <div class="card-body pb-0" v-if="error.messages.length > 0">
                                <div class="alert alert-danger mb-0">
                                    <ul class="mb-0">
                                        <li v-for="(msg, i) in error.messages" :key="i">{{ msg }}</li>
                                    </ul>
                                </div>
                            </div>
                            <div class="card-header">
                                <h4>{{ $t('bp.TrackTechnician.trackYourTechnician') }}</h4>
                            </div>
                            <div class="card-body">
                                <div class="form-group">
                                    <label>{{ $t('bp.TrackTechnician.enterEmail') }}</label>
                                    <input type="email" class="form-control" v-model="form.email" placeholder="email@address.com" :class="{'is-invalid': error.email}" v-on:keyup.enter="findOrder()">
                                    <div class="invalid-feedback" v-if="error.email">{{ $t('bp.TrackTechnician.emailMissing') }}</div>
                                </div>
                                <div class="form-group">
                                    <label>
                                        {{ $t('bp.TrackTechnician.enterOrder') }} &nbsp;
                                        <div class="d-sm-none"></div>
                                        <span class="text-link text-sm cursor-pointer" v-b-popover.hover.top="$t('bp.TrackTechnician.whereToFindOrderHelp')">{{ $t('bp.TrackTechnician.whereToFindOrder') }}</span>
                                    </label>
                                    <input type="text" class="form-control" inputmode="numeric" pattern="[0-9]*" v-model="form.orderRef" placeholder="e.g. 123456" :class="{'is-invalid': error.orderRef}" v-on:keyup.enter="findOrder()">
                                    <div class="invalid-feedback" v-if="error.orderRef">{{ $t('bp.TrackTechnician.orderMissing') }}</div>
                                </div>
                                <button class="btn continue-btn btn-block btn-lg" @click="findOrder()">{{ $t('bp.TrackTechnician.findOrder') }}</button>
                            </div>
                        </div>
                    </div>

                    <div class="container pb-5" v-if="orderTracking && !loading">

                        <div class="card card-body" v-if="orderTracking.tracking.complete">
                            <empty-state
                                statusIcon="success"
                                :title="$t('bp.TrackTechnician.completedTitle')"
                                :subtitle="$t('bp.TrackTechnician.completedSubtitle', { phone: carrierDetails.phone })"
                            ></empty-state>
                        </div>

                        <template v-if="!orderTracking.tracking.complete">

                            <div class="status-hero-card mb-3" :class="'status-' + statusType">
                                <div class="status-inner">
                                    <div class="status-icon-wrap">
                                        <span class="status-icon" v-html="statusIcon"></span>
                                    </div>
                                    <div class="status-text">
                                        <h2 class="status-headline">{{ statusHeadline }}</h2>
                                        <p class="status-subtitle mb-0" v-if="statusSubtitle">{{ statusSubtitle }}</p>
                                        <div class="eta-badge mt-3" v-if="statusType === 'on-the-way' && orderTracking.tracking.current_eta">
                                            <span class="eta-label">{{ $t('bp.TrackTechnician.etaLabel') }}</span>
                                            <span class="eta-time">{{ orderTracking.tracking.current_eta }}</span>
                                        </div>
                                    </div>
                                </div>

                                <div class="first-job-panel mt-4" v-if="isFirstJobPrestart">
                                    <div class="first-job-badge">{{ $t('bp.TrackTechnician.firstJobReadyBadge') }}</div>
                                    <p class="first-job-detail mb-0">{{ $t('bp.TrackTechnician.firstJobReadyDetail', { name: techName }) }}</p>
                                </div>

                                <div class="queue-progress mt-4" v-else-if="hasQueueData && showProgressDots">
                                    <div class="progress-dots">
                                        <template v-for="n in orderTracking.queue.your_repair_position">
                                            <div
                                                :key="n"
                                                class="progress-dot"
                                                :class="dotClass(n)"
                                            >
                                                <span class="dot-label" v-if="n === orderTracking.queue.current_repair_position && n !== orderTracking.queue.your_repair_position">{{ techName }}</span>
                                                <span class="dot-label dot-label-you" v-else-if="n === orderTracking.queue.your_repair_position">{{ $t('bp.TrackTechnician.youLabel') }}</span>
                                            </div>
                                            <div v-if="n < orderTracking.queue.your_repair_position" :key="'line-' + n" class="progress-line" :class="n < orderTracking.queue.current_repair_position ? 'line-done' : 'line-upcoming'"></div>
                                        </template>
                                    </div>
                                    <p class="stops-away-label mb-0" v-if="orderTracking.queue.stops_away > 0">
                                        {{ $tc('bp.TrackTechnician.stopsAway', orderTracking.queue.stops_away, { count: orderTracking.queue.stops_away }) }}
                                    </p>
                                </div>
                            </div>

                            <div class="map-card mb-3" v-show="showMap">
                                <div ref="mapContainer" class="mapbox-map"></div>
                                <div class="map-legend">
                                    <span class="legend-item">
                                        <span class="legend-dot legend-van"></span> {{ techName }}
                                    </span>
                                    <span class="legend-item">
                                        <span class="legend-dot legend-home"></span> {{ $t('bp.TrackTechnician.yourLocation') }}
                                    </span>
                                </div>
                            </div>

                            <div class="card">
                                <div class="card-header mb-0 pb-0">
                                    <h4>{{ $t('bp.TrackTechnician.repairDetails') }}</h4>
                                </div>
                                <div class="card-body pb-2">
                                    <dl>
                                        <dt>{{ $t('bp.TrackTechnician.arrivalTimeslot') }}</dt>
                                        <dd v-if="orderTracking.repair.sameday_booking !== 'sameday'">{{ getHour(orderTracking.repair.arrival_time_start) }} - {{ getHour(orderTracking.repair.arrival_time_end) }}</dd>
                                        <dd v-else>{{ orderTracking.repair.arrival_start }} - {{ orderTracking.repair.arrival_end }}</dd>
                                    </dl>
                                    <dl>
                                        <dt>{{ $t('bp.TrackTechnician.repairAddress') }}</dt>
                                        <dd>{{ orderTracking.repair.address_string }}</dd>
                                    </dl>
                                </div>
                            </div>

                            <div class="card" v-if="orderDevices.length > 0">
                                <div class="card-header">
                                    <h4>{{ $t('bp.TrackTechnician.devicesToBeRepaired') }}</h4>
                                </div>
                                <div class="card-body pb-2">
                                    <div class="media mb-3" v-for="(device, index) in orderDevices" :key="index">
                                        <div class="media-left d-none d-md-block mr-2" v-if="device.model_version">
                                            <img :src="device.model_version.image.path" alt="Device Image" class="img-2x" />
                                        </div>
                                        <div class="media-body">
                                            <h5 v-if="device.model_version">{{ device.model_version.image.title }}</h5>
                                            <div class="price-table table">
                                                <div class="pricing-container">
                                                    <div class="tr" v-for="(repair, ri) in device.details" :key="ri">
                                                        <div class="td">{{ repair.problem.title }}</div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                        </template>
                    </div>

                </div>
            </div>
        </div>
    </div>
</template>
<script>
import { mapActions, mapGetters } from 'vuex'
import mapboxgl from 'mapbox-gl'

export default {

    data() {
        return {
            error: { email: false, messages: [], orderRef: false },
            form: { email: null, orderRef: null },
            loading: true,
            mapMarkers: [],
            mapInstance: null,
            reloadOrderInterval: null,
            reloadVanInterval: null,
            showForm: false,
            showMap: false,
        }
    },

    props: ['email', 'orderRef'],

    mounted() {
        this.loadOrder()
        this.reloadData()
    },

    beforeDestroy() {
        clearInterval(this.reloadVanInterval)
        clearInterval(this.reloadOrderInterval)
        this.clearMapMarkers()
        if (this.mapInstance) {
            this.mapInstance.remove()
            this.mapInstance = null
        }
    },

    computed: {
        ...mapGetters(['carrierDetails', 'orderDevices', 'orderTracking', 'orderTrackingVan']),

        techName() {
            return this.orderTracking && this.orderTracking.technician && this.orderTracking.technician.first_name
                ? this.orderTracking.technician.first_name
                : this.$t('bp.TrackTechnician.technicianFallbackName')
        },

        hasQueueData() {
            return !!(
                this.orderTracking &&
                this.orderTracking.queue &&
                this.orderTracking.queue.your_repair_position &&
                this.orderTracking.queue.current_repair_position !== null &&
                this.orderTracking.queue.current_repair_position !== undefined
            )
        },

        isFirstJobPrestart() {
            return !!(
                this.orderTracking &&
                this.orderTracking.queue &&
                this.orderTracking.tracking &&
                this.statusType === 'preparing' &&
                Number(this.orderTracking.queue.your_repair_position) === 1 &&
                Number(this.orderTracking.queue.current_repair_position) === 1
            )
        },

        showProgressDots() {
            if (!this.hasQueueData) return false
            if (this.statusType === 'on-the-way' || this.isFirstJobPrestart) return false
            const pos = this.orderTracking.queue.your_repair_position
            return pos >= 1 && pos <= 8
        },

        statusType() {
            if (!this.orderTracking || !this.orderTracking.tracking) return 'no-data'
            const tracking = this.orderTracking.tracking
            const queue = this.orderTracking.queue
            if (tracking.can_view_map) return 'on-the-way'
            if (queue && queue.stops_away === 0 && !tracking.started_journey) return 'preparing'
            if (queue && queue.stops_away === 0) return 'almost-there'
            if (queue && queue.stops_away === 1) return 'next-stop'
            if (queue && queue.stops_away > 1) return 'waiting'
            return 'no-data'
        },

        statusIcon() {
            const van = '<svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="1" y="3" width="15" height="13"></rect><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"></polygon><circle cx="5.5" cy="18.5" r="2.5"></circle><circle cx="18.5" cy="18.5" r="2.5"></circle></svg>'
            const lightning = '<svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon></svg>'
            const clock = '<svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>'
            const mapIcon = '<svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6"></polygon><line x1="8" y1="2" x2="8" y2="18"></line><line x1="16" y1="6" x2="16" y2="22"></line></svg>'
            const icons = { 'on-the-way': van, 'preparing': clock, 'almost-there': lightning, 'next-stop': lightning, 'waiting': clock, 'no-data': mapIcon }
            return icons[this.statusType] || icons['no-data']
        },

        statusHeadline() {
            if (!this.orderTracking) return ''
            const name = this.techName
            const queue = this.orderTracking.queue
            switch (this.statusType) {
                case 'on-the-way':    return this.$t('bp.TrackTechnician.statusOnTheWay', { name })
                case 'preparing':     return this.$t('bp.TrackTechnician.statusPreparing', { name })
                case 'almost-there':  return this.$t('bp.TrackTechnician.statusAlmostThere', { name })
                case 'next-stop':     return this.$t('bp.TrackTechnician.statusNextStop')
                case 'waiting':       return this.$t('bp.TrackTechnician.statusQueuePosition', { name, yourRepair: this.ordinal(queue.your_repair_position) })
                default:              return this.$t('bp.TrackTechnician.technicianNamed', { name })
            }
        },

        statusSubtitle() {
            if (!this.orderTracking) return null
            const name = this.techName
            const queue = this.orderTracking.queue
            const tracking = this.orderTracking.tracking
            switch (this.statusType) {
                case 'on-the-way':   return tracking.current_eta ? this.$t('bp.TrackTechnician.statusOnTheWayEta', { eta: tracking.current_eta }) : this.$t('bp.TrackTechnician.statusAlmostThereDetail', { name })
                case 'preparing':    return this.$t('bp.TrackTechnician.statusPreparingDetail', { name })
                case 'almost-there': return this.$t('bp.TrackTechnician.statusAlmostThereDetail', { name })
                case 'next-stop':    return this.$t('bp.TrackTechnician.statusNextStopDetail', { name })
                case 'waiting':      return queue ? this.$t('bp.TrackTechnician.statusQueueDetail', { name, currentRepair: this.ordinal(queue.current_repair_position) }) : null
                default:             return this.$t('bp.TrackTechnician.notHeadingYet')
            }
        },

        queueMessage() { return this.statusSubtitle },
        technicianLabel() { return this.statusHeadline },
    },

    methods: {
        ...mapActions(['retrieveOrderDevices', 'retrieveOrderTracking', 'retrieveTrackingVan']),

        getHour(dateString) {
            if (!dateString) return ''
            const t = dateString.split(/[- :]/)
            const d = new Date(t[0], t[1] - 1, t[2], t[3], t[4], t[5])
            let hours = d.getHours()
            let minutes = d.getUTCMinutes()
            if (hours <= 9) hours = '0' + hours
            if (minutes <= 9) minutes = '0' + minutes
            return hours + ':' + minutes
        },

        findOrder() {
            this.error.email = false
            this.error.orderRef = false
            this.error.messages = []
            if (!this.form.email) this.error.email = true
            if (!this.form.orderRef) this.error.orderRef = true
            if (this.form.email && this.form.orderRef) this.loadOrder()
        },

        ordinal(value) {
            const number = parseInt(value, 10)
            if (!number) return ''
            const mod10 = number % 10
            const mod100 = number % 100
            if (mod10 === 1 && mod100 !== 11) return number + 'st'
            if (mod10 === 2 && mod100 !== 12) return number + 'nd'
            if (mod10 === 3 && mod100 !== 13) return number + 'rd'
            return number + 'th'
        },

        canViewMap() {
            return !!(
                this.orderTracking &&
                this.orderTracking.tracking &&
                this.orderTracking.tracking.can_view_map &&
                this.orderTracking.job_id &&
                this.orderTracking.email
            )
        },

        clearMapMarkers() {
            if (!this.mapMarkers || this.mapMarkers.length === 0) return
            this.mapMarkers.forEach((marker) => marker.remove())
            this.mapMarkers = []
        },

        dotClass(n) {
            const q = this.orderTracking.queue
            if (n < q.current_repair_position) return 'dot-completed'
            if (n === q.current_repair_position && n === q.your_repair_position) return 'dot-current-you'
            if (n === q.current_repair_position) return 'dot-current'
            if (n === q.your_repair_position) return 'dot-you'
            return 'dot-upcoming'
        },

        initMap() {
            if (!this.orderTracking || !this.orderTracking.repair) { this.showMap = false; return }
            const lat = this.orderTracking.repair.latitude
            const lng = this.orderTracking.repair.longitude
            if (!lat || !lng) { this.showMap = false; return }
            this.showMap = true
            // v-show keeps the ref in the DOM, so we can init immediately after next tick
            this.$nextTick(() => {
                if (!this.$refs.mapContainer) { this.showMap = false; return }
                if (this.mapInstance) { this.mapInstance.remove(); this.mapInstance = null }
                const token = process.env.VUE_APP_MAPBOX_TOKEN
                if (!token) { console.warn('VUE_APP_MAPBOX_TOKEN not set'); this.showMap = false; return }
                mapboxgl.accessToken = token
                this.mapInstance = new mapboxgl.Map({
                    container: this.$refs.mapContainer,
                    style: 'mapbox://styles/mapbox/streets-v12',
                    center: [lng, lat],
                    zoom: 12,
                    attributionControl: false,
                })
                this.mapInstance.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'top-right')
                this.mapInstance.on('load', () => { this.addMapMarkers() })
                this.mapInstance.on('error', (e) => { console.warn('Mapbox error:', e) })
            })
        },

        addMapMarkers() {
            if (!this.mapInstance) return
            this.clearMapMarkers()
            const repairLat = this.orderTracking.repair.latitude
            const repairLng = this.orderTracking.repair.longitude
            const homeEl = document.createElement('div')
            homeEl.className = 'map-marker-home'
            homeEl.innerHTML = '<div class="map-marker-home__circle"><svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z"/></svg></div><div class="map-marker-home__tip"></div>'
            const homeMarker = new mapboxgl.Marker(homeEl, { anchor: 'bottom' }).setLngLat([repairLng, repairLat]).setPopup(new mapboxgl.Popup({ offset: 40 }).setText(this.$t('bp.TrackTechnician.yourLocation'))).addTo(this.mapInstance)
            this.mapMarkers.push(homeMarker)
            const bounds = new mapboxgl.LngLatBounds()
            bounds.extend([repairLng, repairLat])
            if (this.orderTrackingVan && this.orderTrackingVan.latitude && this.orderTrackingVan.longitude) {
                const vanLat = parseFloat(this.orderTrackingVan.latitude)
                const vanLng = parseFloat(this.orderTrackingVan.longitude)
                const vanEl = document.createElement('div')
                vanEl.className = 'map-marker-van'
                vanEl.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M20 8h-3V4H3c-1.1 0-2 .9-2 2v11h2c0 1.66 1.34 3 3 3s3-1.34 3-3h6c0 1.66 1.34 3 3 3s3-1.34 3-3h2v-5l-3-4zm-.5 1.5l1.96 2.5H17V9.5h2.5zM6 18c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1zm2.22-3c-.55-.61-1.35-1-2.22-1s-1.67.39-2.22 1H3V6h12v9H8.22zM18 18c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1z"/></svg>'
                const vanMarker = new mapboxgl.Marker(vanEl).setLngLat([vanLng, vanLat]).setPopup(new mapboxgl.Popup({ offset: 25 }).setText(this.techName)).addTo(this.mapInstance)
                this.mapMarkers.push(vanMarker)
                bounds.extend([vanLng, vanLat])
            }
            if (!bounds.isEmpty()) {
                this.mapInstance.fitBounds(bounds, { padding: { top: 60, right: 60, bottom: 60, left: 60 }, maxZoom: 14 })
            }
        },

        loadOrder() {
            this.loading = true
            this.showForm = false
            this.showMap = false
            if (this.orderTracking) {
                if (this.canViewMap()) {
                    this.retrieveTrackingVan({ jobId: this.orderTracking.job_id, email: this.orderTracking.email }).then(() => { this.initMap() })
                }
                this.loading = false
                return
            }
            const orderRef = this.form.orderRef ? this.form.orderRef : this.$route.params.orderRef
            const email = this.form.email ? this.form.email : this.$route.params.email
            if (orderRef && email) {
                this.retrieveOrderTracking({ orderRef, email })
                    .then((trackingPayload) => {
                        if (email !== trackingPayload.email || orderRef !== trackingPayload.order_ref) {
                            this.loading = false; this.showForm = true
                            this.error.messages.push(this.$t('bp.TrackTechnician.orderLookupFailed'))
                            return
                        }
                        this.showForm = false
                        this.retrieveOrderDevices({ orderRef, email }).then(() => {
                            if (this.canViewMap()) {
                                this.retrieveTrackingVan({ jobId: this.orderTracking.job_id, email: this.orderTracking.email }).then(() => { this.initMap() })
                            }
                            this.loading = false
                        })
                    })
                    .catch(error => {
                        this.showForm = true
                        let message = this.$t('bp.TrackTechnician.orderLookupFailed')
                        if (error && error.response && error.response.data && error.response.data.messages && error.response.data.messages.length > 0) {
                            message = error.response.data.messages[0]
                        }
                        this.error.messages.push(message)
                        this.loading = false
                    })
            } else {
                if (Object.keys(this.$route.params).length > 0 && (!orderRef || !email)) {
                    this.error.messages.push(this.$t('bp.TrackTechnician.routeParamsMissing'))
                }
                this.loading = false
                this.showForm = true
            }
        },

        reloadData() {
            const self = this
            this.reloadVanInterval = setInterval(function () {
                if (self.canViewMap()) {
                    self.retrieveTrackingVan({ jobId: self.orderTracking.job_id, email: self.orderTracking.email }).then(() => { self.addMapMarkers() })
                }
            }, 60000)
            this.reloadOrderInterval = setInterval(function () {
                const orderRef = self.form.orderRef ? self.form.orderRef : self.$route.params.orderRef
                const email = self.form.email ? self.form.email : self.$route.params.email
                if (!orderRef || !email) return
                self.retrieveOrderTracking({ orderRef, email })
                if (!self.canViewMap()) self.showMap = false
            }, 120000)
        },
    },
}
</script>
<style>
@import '~mapbox-gl/dist/mapbox-gl.css';

.map-marker-van {
    width: 42px; height: 42px;
    background: #3200be; border-radius: 50%; border: 3px solid #fff;
    display: flex; align-items: center; justify-content: center;
    color: #fff; box-shadow: 0 3px 12px rgba(50,0,190,0.5);
    cursor: pointer;
}

/* Pin shape: circle on top of a downward triangle */
.map-marker-home {
    position: relative;
    width: 30px; height: 42px;
    display: flex; flex-direction: column; align-items: center;
    cursor: pointer; filter: drop-shadow(0 3px 6px rgba(229,62,62,0.5));
}
.map-marker-home__circle {
    width: 30px; height: 30px;
    background: #e53e3e; border-radius: 50%; border: 3px solid #fff;
    display: flex; align-items: center; justify-content: center;
    color: #fff; flex-shrink: 0;
}
.map-marker-home__tip {
    width: 0; height: 0;
    border-left: 7px solid transparent;
    border-right: 7px solid transparent;
    border-top: 12px solid #e53e3e;
    margin-top: -1px;
}
</style>

<style scoped>
.track-tech-page { min-height: 100vh; background: #f8f9fb; }

/* ── Status card: consistent white card matching Likewize site style ── */
.status-hero-card {
    background: #fff;
    border-radius: 10px;
    border: 1px solid #dbdbdb;
    border-left: 5px solid #3200be;
    padding: 24px 20px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.06);
}

/* Colour the left border accent differently per state */
.status-on-the-way   { border-left-color: #3200be; }
.status-preparing    { border-left-color: #0c2340; }
.status-almost-there { border-left-color: #3200be; }
.status-next-stop    { border-left-color: #ff96ff; }
.status-waiting      { border-left-color: #0c2340; }
.status-no-data      { border-left-color: #aaa; }

.status-inner { display: flex; align-items: flex-start; gap: 16px; flex-wrap: wrap; }

.status-icon-wrap {
    flex-shrink: 0; width: 56px; height: 56px;
    background: #3200be; border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
    color: #fff;
}
.status-on-the-way   .status-icon-wrap { background: #3200be; animation: pulse-ring 2s infinite; }
.status-preparing    .status-icon-wrap { background: #0c2340; }
.status-almost-there .status-icon-wrap { background: #3200be; }
.status-next-stop    .status-icon-wrap { background: #ff96ff; }
.status-waiting      .status-icon-wrap { background: #0c2340; }
.status-no-data      .status-icon-wrap { background: #6b7280; }

@keyframes pulse-ring {
    0%   { box-shadow: 0 0 0 0 rgba(50,0,190,0.3); }
    70%  { box-shadow: 0 0 0 12px rgba(50,0,190,0); }
    100% { box-shadow: 0 0 0 0 rgba(50,0,190,0); }
}

.status-text { flex: 1; min-width: 0; }

.status-headline {
    font-size: 1.15rem; font-weight: 700; margin-bottom: 4px;
    color: #0c2340; line-height: 1.3;
}
@media (min-width: 480px) { .status-headline { font-size: 1.3rem; } }

.status-subtitle { font-size: 0.95rem; color: #555; line-height: 1.5; margin: 0; }

.eta-badge {
    display: inline-flex; align-items: center; gap: 10px;
    background: #f0ecff; border: 1px solid #c4b5fd;
    border-radius: 24px; padding: 6px 18px; margin-top: 10px;
}
.eta-label { font-size: 0.75rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em; color: #3200be; }
.eta-time  { font-size: 1.3rem; font-weight: 700; color: #3200be; }

.first-job-panel {
    margin-top: 14px;
    border-top: 1px solid #eee;
    padding-top: 14px;
}

.first-job-badge {
    display: inline-flex;
    align-items: center;
    background: #f3efff;
    color: #3200be;
    border: 1px solid #d7cfff;
    border-radius: 999px;
    padding: 4px 12px;
    font-size: 0.78rem;
    font-weight: 700;
    margin-bottom: 8px;
}

.first-job-detail {
    color: #555;
    font-size: 0.92rem;
    line-height: 1.45;
}

/* ── Queue progress dots ── */
.queue-progress { padding-top: 16px; border-top: 1px solid #eee; margin-top: 16px; }

.progress-dots { display: flex; align-items: center; flex-wrap: nowrap; gap: 0; padding-bottom: 28px; overflow-x: auto; }

.progress-dot {
    position: relative; width: 32px; height: 32px; border-radius: 50%;
    display: flex; align-items: center; justify-content: center; flex-shrink: 0;
    border: 2px solid #ddd; background: #f5f5f5;
}
.progress-line { flex: 1; height: 3px; min-width: 12px; max-width: 36px; background: #e0e0e0; }
.line-done { background: #3200be; }

.dot-completed { background: #3200be; border-color: #3200be; }
.dot-completed::after { content: ''; width: 8px; height: 8px; background: #fff; border-radius: 50%; }
.dot-current { background: #3200be; border-color: #3200be; box-shadow: 0 0 0 4px rgba(50,0,190,0.15); }
.dot-current-you { background: #ff96ff; border-color: #ff96ff; box-shadow: 0 0 0 4px rgba(255,150,255,0.2); }
.dot-you { background: #fff; border-color: #3200be; border-width: 3px; }
.dot-upcoming { background: #f5f5f5; border-color: #ddd; }

.dot-label {
    position: absolute; top: calc(100% + 6px); left: 50%; transform: translateX(-50%);
    white-space: nowrap; font-size: 0.68rem; font-weight: 600;
    color: #3200be; text-align: center;
}
.stops-away-label { font-size: 0.9rem; font-weight: 600; color: #0c2340; margin-top: 8px !important; }

/* ── Map ── */
.map-card { border-radius: 10px; overflow: hidden; border: 1px solid #dbdbdb; box-shadow: 0 2px 8px rgba(0,0,0,0.06); }
.mapbox-map { height: 320px; width: 100%; }
@media (min-width: 768px) { .mapbox-map { height: 400px; } }

.map-legend {
    display: flex; gap: 20px; padding: 10px 16px;
    background: #fff; border-top: 1px solid #eee;
}
.legend-item { display: flex; align-items: center; gap: 6px; font-size: 0.82rem; color: #555; font-weight: 500; }
.legend-dot { width: 12px; height: 12px; border-radius: 50%; display: inline-block; }
.legend-van  { background: #3200be; }
.legend-home { background: #e53e3e; }
</style>